// TODO: vitest component test (pages are covered by Playwright e2e)
import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, FormActions, Input, Select, Textarea } from '@/components/ui'
import {
  useEquipmentDetail,
  useCreateEquipment,
  useUpdateEquipment,
} from '@/hooks/useCmms'
import { useProperties } from '@/hooks/useProperties'
import { utcDateOnly } from '@/lib/date'

interface EquipmentFormData {
  name: string
  category: string
  status: string
  property_id: string
  location: string
  manufacturer: string
  model_number: string
  serial_number: string
  installed_at: string
  warranty_expires_at: string
  notes: string
}

export function EquipmentFormPage() {
  const { t } = useTranslation('cmms')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const isEditing = !!id
  const equipmentId = Number(id)
  const navigate = useNavigate()

  const { data: existingEquipment, isLoading: isLoadingEquipment } = useEquipmentDetail(
    isEditing ? equipmentId : 0
  )
  const createEquipment = useCreateEquipment()
  const updateEquipment = useUpdateEquipment()
  const { data: propertiesData } = useProperties()

  const properties = propertiesData?.data || []

  const categoryOptions = [
    { value: '', label: t('form.select_category') },
    ...(['hvac', 'plumbing', 'electrical', 'elevator', 'fire_safety', 'security', 'general'] as const).map(
      (c) => ({ value: c, label: t(`categories.${c}`) })
    ),
  ]

  const statusOptions = [
    { value: 'operational', label: t('equipment_status.operational') },
    { value: 'needs_repair', label: t('equipment_status.needs_repair') },
    { value: 'out_of_service', label: t('equipment_status.out_of_service') },
    { value: 'decommissioned', label: t('equipment_status.decommissioned') },
  ]

  const propertyOptions = [
    { value: '', label: t('form.select_property') },
    ...properties.map((p) => ({ value: String(p.id), label: p.name })),
  ]

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EquipmentFormData>({
    defaultValues: {
      name: '',
      category: '',
      status: 'operational',
      property_id: '',
      location: '',
      manufacturer: '',
      model_number: '',
      serial_number: '',
      installed_at: '',
      warranty_expires_at: '',
      notes: '',
    },
  })

  useEffect(() => {
    if (isEditing && existingEquipment) {
      reset({
        name: existingEquipment.name,
        category: existingEquipment.category,
        status: existingEquipment.status,
        property_id: existingEquipment.property_id ? String(existingEquipment.property_id) : '',
        location: existingEquipment.location || '',
        manufacturer: existingEquipment.manufacturer || '',
        model_number: existingEquipment.model_number || '',
        serial_number: existingEquipment.serial_number || '',
        installed_at: existingEquipment.installed_at
          ? utcDateOnly(new Date(existingEquipment.installed_at))
          : '',
        warranty_expires_at: existingEquipment.warranty_expires_at
          ? utcDateOnly(new Date(existingEquipment.warranty_expires_at))
          : '',
        notes: existingEquipment.notes || '',
      })
    }
  }, [isEditing, existingEquipment, reset])

  const onSubmit = async (formData: EquipmentFormData) => {
    const payload = {
      name: formData.name,
      category: formData.category,
      status: formData.status,
      property_id: Number(formData.property_id),
      location: formData.location || undefined,
      manufacturer: formData.manufacturer || undefined,
      model_number: formData.model_number || undefined,
      serial_number: formData.serial_number || undefined,
      installed_at: formData.installed_at || undefined,
      warranty_expires_at: formData.warranty_expires_at || undefined,
      notes: formData.notes || undefined,
    }

    if (isEditing) {
      await updateEquipment.mutateAsync({ id: equipmentId, ...payload })
      navigate(`/equipment/${equipmentId}`)
    } else {
      const result = await createEquipment.mutateAsync(payload)
      navigate(`/equipment/${result.id}`)
    }
  }

  if (isEditing && isLoadingEquipment) {
    return (
      <div className="space-y-6">
        <Card><div className="h-64 animate-pulse bg-base-200 rounded" /></Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs
        items={[
          { label: t('equipment.title'), href: '/equipment' },
          { label: isEditing ? t('equipment.edit') : t('equipment.new') },
        ]}
      />

      <PageHeader
        title={isEditing ? t('form.title_edit_equipment') : t('form.title_new_equipment')}
        description={isEditing ? t('form.description_edit_equipment') : t('form.description_new_equipment')}
      />

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <Input
              label={t('labels.name')}
              error={errors.name?.message}
              {...register('name', { required: tc('errors.required') })}
            />
            <Select
              label={t('labels.category')}
              options={categoryOptions}
              error={errors.category?.message}
              {...register('category', { required: tc('errors.required') })}
            />
            <Select
              label={t('labels.status')}
              options={statusOptions}
              {...register('status')}
            />
            <Select
              label={t('labels.property')}
              options={propertyOptions}
              {...register('property_id', { required: true })}
            />
            <Input
              label={t('labels.location')}
              {...register('location')}
            />
            <Input
              label={t('labels.manufacturer')}
              {...register('manufacturer')}
            />
            <Input
              label={t('labels.model_number')}
              {...register('model_number')}
            />
            <Input
              label={t('labels.serial_number')}
              {...register('serial_number')}
            />
            <Input
              label={t('labels.installed_at')}
              type="date"
              {...register('installed_at')}
            />
            <Input
              label={t('labels.warranty_expires_at')}
              type="date"
              {...register('warranty_expires_at')}
            />
          </div>

          <Textarea
            label={t('labels.notes')}
            rows={3}
            {...register('notes')}
          />

          <FormActions>
            <Button type="button" variant="ghost" onClick={() => navigate(-1)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              loading={isEditing ? updateEquipment.isPending : createEquipment.isPending}
            >
              {isEditing ? tc('actions.save_changes') : tc('actions.create')}
            </Button>
          </FormActions>
        </form>
      </Card>
    </div>
  )
}
